import * as LabelPrimitive from '@radix-ui/react-label'
import { Button, cn } from '@today/ui-primitives'
import { type InputHTMLAttributes, type ReactNode, useId } from 'react'

/**
 * Configuration for an optional inline action button within a {@link Field}.
 *
 * @property label - Button text (e.g., "Clear", "Show")
 * @property onPress - Optional click handler
 *
 * @example
 * ```tsx
 * const clearAction: FieldAction = {
 *   label: 'Clear',
 *   onPress: () => setValue('')
 * }
 * ```
 */
export interface FieldAction {
	label: string
	onPress?: () => void
}

/**
 * Props for the {@link Field} component.
 *
 * Extends native HTML input attributes with custom props for label, description, and action.
 *
 * @property label - Visible label text (required, linked to input via htmlFor)
 * @property description - Optional helper text below input (linked via aria-describedby)
 * @property action - Optional inline button (demonstrates primitives composition)
 * @property className - Optional Tailwind classes for the container
 * @property type - HTML input type (text, email, password, etc.)
 * @property placeholder - Placeholder text
 * @property value - Controlled value
 * @property onChange - Change handler
 * @property required - Native HTML required attribute
 * @property disabled - Native HTML disabled attribute
 *
 * @example
 * ```tsx
 * <Field
 *   label="Email"
 *   type="email"
 *   placeholder="you@example.com"
 *   description="We'll never share your email"
 * />
 * ```
 */
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
	/** Visible, accessible label associated with the input. */
	label: string
	/** Optional helper text linked to the input via `aria-describedby`. */
	description?: ReactNode
	/** Optional inline action rendered as a primitives-layer Button. */
	action?: FieldAction
}

/**
 * Labeled text input molecule.
 *
 * @remarks
 * - **Composition:** Demonstrates components → primitives dependency.
 *   Composes Radix `<Label>` primitive with `<Button>` from `@today/ui-primitives`.
 * - **Accessibility:** Auto-generated unique ID links label, input, and description.
 *   Uses `htmlFor` and `aria-describedby` for screen reader support.
 * - **Styling:** All colors, spacing, and borders use design tokens from `@today/tokens`.
 * - **Action Button:** Optional inline button (size: 'sm', variant: 'muted') for
 *   actions like "Clear" or "Show password".
 *
 * @example Basic usage
 * ```tsx
 * import { Field } from '@today/ui-components'
 *
 * function LoginForm() {
 *   return (
 *     <form>
 *       <Field
 *         label="Email"
 *         type="email"
 *         placeholder="you@example.com"
 *         required
 *       />
 *       <Field
 *         label="Password"
 *         type="password"
 *         description="At least 8 characters"
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With inline action button
 * ```tsx
 * const [query, setQuery] = useState('')
 *
 * <Field
 *   label="Search"
 *   value={query}
 *   onChange={(e) => setQuery(e.target.value)}
 *   action={{
 *     label: 'Clear',
 *     onPress: () => setQuery('')
 *   }}
 * />
 * ```
 *
 * @see packages/ui-components/src/field/field.stories.tsx for visual examples
 * @see {@link FieldAction} for action button configuration
 */
export function Field({ label, description, action, className, ...inputProps }: FieldProps) {
	const id = useId()
	const descriptionId = description ? `${id}-description` : undefined
	return (
		<div className={cn('flex flex-col gap-1.5', className)}>
			<LabelPrimitive.Root htmlFor={id} className="text-ds-sm font-medium text-ds-text-muted">
				{label}
			</LabelPrimitive.Root>
			<div className="flex items-center gap-2">
				<input
					id={id}
					aria-describedby={descriptionId}
					className="flex-1 rounded-ds-md border border-ds-border px-3 py-2 text-ds-base"
					{...inputProps}
				/>
				{action ? (
					<Button size="sm" variant="muted" onPress={action.onPress}>
						{action.label}
					</Button>
				) : null}
			</div>
			{description ? (
				<p id={descriptionId} className="text-ds-sm text-ds-text-muted">
					{description}
				</p>
			) : null}
		</div>
	)
}
